import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Input, Row, Typography } from 'antd';
import { LucideKeyRound } from 'lucide-react';
import { TbPlus } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
    EmptySection,
    VirtualizedList as List,
    OneTimeCredentialModal,
    PlanTag,
    SkeletonList,
    type OneTimeCredentialDisclosure,
} from '@/shared';

import { maskCredential } from '../../components/credentialPreview';
import APIKeyListItem, { APIKeyListRecord } from './APIKeyListItem';
import CreateAPIKeyModal from './CreateAPIKeyModal';

const APIKeys: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const [apiKeys, setAPIKeys] = useState<APIKeyListRecord[]>([]);
    const [credentialDisclosure, setCredentialDisclosure] = useState<OneTimeCredentialDisclosure>();
    const [query, setQuery] = useState('');
    const filteredAPIKeys = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return apiKeys;
        }

        return apiKeys.filter(({ name, integration }) =>
            [name, integration].some((value) => value?.toLowerCase().includes(normalizedQuery)),
        );
    }, [apiKeys, query]);

    useEffect(() => {
        let active = true;

        authorizedAxiosInstance
            .get<APIKeyListRecord[]>('/api/apikeys')
            .then(({ data }) => {
                if (active) {
                    setAPIKeys(data);
                }
            })
            .catch(captureException)
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    const { hasFeature } = useEntitlements();
    const canUseAPIKeys = hasFeature('apiKeys');

    const { token } = useAntDesignTheme();

    return (
        <>
            <Row align='top' wrap={false}>
                <Col flex='auto'>
                    <Typography.Title level={5}>
                        <LucideKeyRound size='1rem' style={{ verticalAlign: '-0.125rem', marginRight: '0.5rem' }} />
                        API Keys
                        <PlanTag plan='enterprise' style={{ marginLeft: '0.5rem' }} />
                    </Typography.Title>
                    <Typography.Paragraph type='secondary'>
                        Extend Countercyclical's functionality via our programmable interface.
                    </Typography.Paragraph>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col flex='auto'>
                    <Input
                        allowClear
                        value={query}
                        placeholder='Search API Keys...'
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </Col>
                <Col flex='none'>
                    <Button onClick={() => setOpen(true)} type='dashed' disabled={!canUseAPIKeys}>
                        <TbPlus size='1rem' style={{ verticalAlign: '-0.15rem' }} />
                        Add API Key
                    </Button>
                </Col>
            </Row>
            <CreateAPIKeyModal
                open={open}
                setOpen={setOpen}
                onCreated={(apiKey) => {
                    const record = apiKey as APIKeyListRecord;
                    const keyPreview = record.keyPreview || maskCredential(apiKey.key, { prefixLength: 11 });

                    setAPIKeys((items) => [...items, { ...record, key: keyPreview, keyPreview }]);
                    setCredentialDisclosure({
                        copyLabel: 'Copy API Key',
                        description:
                            'This is the only time Countercyclical will display the complete API key. Copy it before closing this modal.',
                        title: 'API Key Created',
                        values: [{ label: 'API Key', value: apiKey.key }],
                    });
                }}
            />
            <OneTimeCredentialModal
                disclosure={credentialDisclosure}
                onClose={() => setCredentialDisclosure(undefined)}
            />
            {filteredAPIKeys.length !== 0 && !loading ? (
                <>
                    <List
                        dataSource={filteredAPIKeys}
                        renderItem={(ak: APIKeyListRecord) => {
                            return (
                                <APIKeyListItem
                                    key={ak?.id}
                                    item={ak}
                                    onCredentialGenerated={(credential, apiKeyName) =>
                                        setCredentialDisclosure({
                                            copyLabel: 'Copy API Key',
                                            description: `The previous key for ${apiKeyName} has been revoked. Copy this replacement before closing the modal because Countercyclical cannot display it again.`,
                                            title: 'API Key Rotated',
                                            values: [{ label: 'API Key', value: credential }],
                                        })
                                    }
                                    onDeleted={(apiKeyId) =>
                                        setAPIKeys((items) => items.filter((item) => item.id !== apiKeyId))
                                    }
                                    onUpdated={(updatedAPIKey) =>
                                        setAPIKeys((items) =>
                                            items.map((item) => (item.id === updatedAPIKey.id ? updatedAPIKey : item)),
                                        )
                                    }
                                />
                            );
                        }}
                    />
                    <Divider />
                </>
            ) : loading ? (
                <SkeletonList listLength={8} maxHeight='20rem' minHeight='20rem' />
            ) : (
                <EmptySection
                    height='25rem'
                    icon={<LucideKeyRound style={{ color: token.colorTextTertiary }} />}
                    label={query ? 'No Matching API Keys' : 'No API Keys'}
                    description={
                        query
                            ? 'No API keys match the current search.'
                            : 'Create an API key above to programmatically streamline your investment research and due diligence.'
                    }
                />
            )}
        </>
    );
};

export default APIKeys;
